import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { CrmDataProvider } from "../providers/types";

/**
 * 'Personal': one confirmation sentence and two buttons, no fields. Confirming calls
 * `mark_whatsapp_number_personal`; cancelling changes nothing. When the server refuses (an open
 * lead) its Spanish message is shown inline, the dialog stays open and nothing leaves the tray.
 */
export const MarkPersonalDialog = ({
  conversationId,
  open,
  onOpenChange,
}: {
  conversationId: Identifier;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const onConfirm = async () => {
    setSaving(true);
    setFailure(null);
    try {
      await dataProvider.markWhatsAppNumberPersonal(conversationId);
      await queryClient.invalidateQueries({ queryKey: ["whatsapp"] });
      notify("crm.whatsapp.unclassified.personal.success", {
        type: "success",
      });
      onOpenChange(false);
    } catch (error) {
      setFailure(
        error instanceof Error && error.message
          ? error.message
          : translate("crm.whatsapp.unclassified.personal.error"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setFailure(null);
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {translate("crm.whatsapp.unclassified.personal.title")}
          </DialogTitle>
          <DialogDescription>
            {translate("crm.whatsapp.unclassified.personal.sentence")}
          </DialogDescription>
        </DialogHeader>
        {failure ? (
          <p role="alert" className="text-[15px] text-destructive">
            {failure}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="h-11"
            onClick={() => onOpenChange(false)}
          >
            {translate("crm.whatsapp.unclassified.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="h-11"
            disabled={saving}
            onClick={onConfirm}
          >
            {translate("crm.whatsapp.unclassified.personal.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
